import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import * as Bixby from '$/core';
import { History } from './../../../services';
import { AppState } from '$/store';
import { getPortfolio } from '$/store/portfolio/actions';
import {
  getIsCurrentUserAdminState,
  getIsCurrentUserCreditSightsAnalystState,
  getIsCurrentUserCreditSightsOpsState,
  getIsCurrentUserRatingsAnalystState,
  getIsCurrentUserSuFAnalystState,
} from '$/store/profile/selectors';
import HeadingBlock, { IDropdown } from '$/components/global/heading';
import Loading from '$/components/loading/Loading';
import NoDataFound from '$/components/global/noData';
import PortfolioDropDown from './PortfolioDropDown';
import './index.scss';
import { PortfolioGrid } from '$/components/portfolio-grid';
import {
  getHasBixbyComplianceFeatureFlagState,
  getHasPublicCompaniesFeatureFlagState,
  getHasUsEuroGroupingFeatureFlagState,
} from '$/store/login/selectors';
import { exportPortfolio } from '$/store/export/actions';

interface IPortfolioProps {
  hasPublicCompaniesFeatureFlag: boolean;
  hasBixbyCompliance: boolean;
  isLoading: boolean;
  isAdmin: boolean;
  isCreditSightsOpsSufOrRatings: boolean;
  isCreditSightsAnalyst: boolean;
  isSuFAnalyst: boolean;
  isRatingsAnalyst: boolean;
  portfolio?: Bixby.Models.CompanyPortfolio[];
  hasBixbyComplianceFeatureFlag: boolean;
  hasUsEuroGroupingFeatureFlag: boolean;
  dispatchGetPortfolio(myPortfolioEnabled: boolean): () => Promise<void>;
  dispatchHandleExport: (myPortfolioEnabled: boolean) => Promise<void>;
}

const Portfolio: React.FC<IPortfolioProps> = props => {
  const portfolioPath = '/portfolio';
  const myPortfolioPath = '/myportfolio';

  const {
    isLoading,
    isAdmin,
    portfolio,
    dispatchGetPortfolio,
    hasPublicCompaniesFeatureFlag,
    hasBixbyComplianceFeatureFlag,
    hasUsEuroGroupingFeatureFlag,
    isCreditSightsOpsSufOrRatings,
    isSuFAnalyst,
    isRatingsAnalyst,
    isCreditSightsAnalyst,
    dispatchHandleExport,
  } = props;

  const isAdminOrCreditSightsOpsSufOrRatings = isAdmin || isCreditSightsOpsSufOrRatings;

  const [gridView, setGridView] = useState(
    History.location.pathname === myPortfolioPath
      ? Bixby.Constants.PORTFOLIO_VIEW_MINE
      : Bixby.Constants.PORTFOLIO_VIEW_ALL
  );

  const myPortfolioEnabled = useMemo(() => gridView === Bixby.Constants.PORTFOLIO_VIEW_MINE, [gridView]);
  const isSyndicationPortfolioView = useMemo(() => gridView === Bixby.Constants.PORTFOLIO_VIEW_SYNDICATION, [gridView]);

  useEffect(() => {
    dispatchGetPortfolio(myPortfolioEnabled);
  }, [dispatchGetPortfolio, myPortfolioEnabled]);

  const onSetGridView = (selectedOption: string) => {
    const viewMyPortfolio = selectedOption === Bixby.Constants.PORTFOLIO_VIEW_MINE;
    setGridView(selectedOption);
    History.push(viewMyPortfolio ? myPortfolioPath : portfolioPath);
  };

  const dropdown: IDropdown = {
    enabled: true,
    component: (
      <div className="portfolio_dropdown_wrapper">
        <PortfolioDropDown gridView={gridView} onSetGridView={onSetGridView} />
      </div>
    ),
  };

  const filteredPortfolio = useMemo(() => {
    if (!portfolio) return portfolio;

    if (gridView === Bixby.Constants.PORTFOLIO_VIEW_SYNDICATION) {
      return portfolio.filter(_ => _.isSyndication);
    } else if (!hasPublicCompaniesFeatureFlag || gridView === Bixby.Constants.PORTFOLIO_VIEW_PRIVATE) {
      return portfolio.filter(_ => !_.isSECPublic);
    } else if (gridView === Bixby.Constants.PORTFOLIO_VIEW_PUBLIC) {
      return portfolio.filter(_ => _.isSECPublic);
    } else if (gridView === Bixby.Constants.PORTFOLIO_VIEW_PUBLIC_EXCLUDE_DEFUNCT) {
      return portfolio.filter(_ => _.isSECPublic && !_.isDelisted);
    } else {
      return portfolio;
    }
  }, [gridView, hasPublicCompaniesFeatureFlag, portfolio]);

  const handleExport = useCallback(
    () => dispatchHandleExport(myPortfolioEnabled),
    [myPortfolioEnabled, dispatchHandleExport]
  );

  if (isLoading) {
    return <Loading pageLevel />;
  } else if (filteredPortfolio && !Bixby.Utils.isNullOrEmpty(filteredPortfolio)) {
    return (
      <React.Fragment>
        <section>
          <HeadingBlock
            title={myPortfolioEnabled ? 'MyPortfolio' : 'Portfolio'}
            dropdown={dropdown}
            exportEnabled
            handleExport={handleExport}
          />
          <div className="portfolio_data_grid">
            <div className="grid_container">
              <div className="grid ag-theme-alpine">
                <PortfolioGrid
                  gridData={filteredPortfolio}
                  isAdminOrCreditSightsOpsSufOrRatings={isAdminOrCreditSightsOpsSufOrRatings}
                  hasPublicCompaniesFeatureFlag={hasPublicCompaniesFeatureFlag}
                  hasBixbyComplianceFeatureFlag={hasBixbyComplianceFeatureFlag}
                  isSyndicationPortfolioView={isSyndicationPortfolioView}
                  hasUsEuroGroupingFeatureFlag={hasUsEuroGroupingFeatureFlag}
                  heatMapActive={false}
                />
              </div>
            </div>
          </div>
        </section>
      </React.Fragment>
    );
  } else {
    if (myPortfolioEnabled) {
      return (
        <React.Fragment>
          <section>
            <HeadingBlock title="MyPortfolio" dropdown={dropdown} exportEnabled handleExport={handleExport} />
            <div className="portfolio_no_data">
              {isAdmin && <h3>Admin User</h3>}
              {isCreditSightsAnalyst && <h3>CreditSights User</h3>}
              {isSuFAnalyst && <h3>Suf Analyst</h3>}
              {isRatingsAnalyst && <h3>Rating Analyst</h3>}
              {!isAdminOrCreditSightsOpsSufOrRatings && (
                <p>
                  To customize the list of names in MyPortfolio, go to{' '}
                  <Link to="/settings/my-companies">Settings {'>'} My Companies</Link>
                </p>
              )}
            </div>
          </section>
        </React.Fragment>
      );
    } else {
      return (
        <React.Fragment>
          <section>
            <HeadingBlock title="Portfolio" dropdown={dropdown} exportEnabled handleExport={handleExport} />
            <div className="portfolio_no_data">
              <NoDataFound dataType="companies" />
            </div>
          </section>
        </React.Fragment>
      );
    }
  }
};

const mapStateToProps = (state: AppState) => {
  const { isAdmin } = getIsCurrentUserAdminState(state);
  const { isCreditSightsAnalyst } = getIsCurrentUserCreditSightsAnalystState(state);
  const { isSuFAnalyst } = getIsCurrentUserSuFAnalystState(state);
  const { isCreditSightsOps } = getIsCurrentUserCreditSightsOpsState(state);
  const { hasPublicCompaniesFeatureFlag } = getHasPublicCompaniesFeatureFlagState(state);
  const { hasBixbyComplianceFeatureFlag } = getHasBixbyComplianceFeatureFlagState(state);
  const { hasUsEuroGroupingFeatureFlag } = getHasUsEuroGroupingFeatureFlagState(state);
  const { isRatingsAnalyst } = getIsCurrentUserRatingsAnalystState(state);

  return {
    hasBixbyComplianceFeatureFlag: hasBixbyComplianceFeatureFlag,
    hasPublicCompaniesFeatureFlag,
    isLoading: state.portfolioState.isLoading,
    isAdmin,
    isCreditSightsOpsSufOrRatings: isCreditSightsAnalyst || isCreditSightsOps || isSuFAnalyst || isRatingsAnalyst,
    isCreditSightsAnalyst: isCreditSightsAnalyst || isCreditSightsOps,
    isSuFAnalyst,
    isRatingsAnalyst,
    portfolio: state.portfolioState.portfolio,
    hasUsEuroGroupingFeatureFlag,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchGetPortfolio: (myPortfolioEnabled: boolean) => dispatch(getPortfolio(myPortfolioEnabled)),
  dispatchHandleExport: (myPortfolioEnabled: boolean) => dispatch(exportPortfolio(myPortfolioEnabled)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Portfolio);
